import { Link } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { SITE } from '@/data/site';
import { faqGroups } from '@/data/faq';

const allItems = faqGroups.flatMap((g) => g.items);

export default function FaqPage() {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: allItems.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: SITE.domain },
        { '@type': 'ListItem', position: 2, name: '常见问题', item: `${SITE.domain}/faq` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`常见问题 - ${SITE.name}阅读与内容说明`}
        description={`关于${SITE.name}的内容方向、阅读方式与参与方式的常见问题解答，包含是否需要付费、如何搜索话题、怎样反馈内容错误等说明。`}
        keywords="吃瓜网常见问题,阅读说明,内容反馈,免费阅读"
        path="/faq"
        schema={schema}
      />

      <div className="max-w-container mx-auto px-4 md:px-8 pt-8">
        <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '常见问题' }]} />
      </div>

      <section className="max-w-container mx-auto px-4 md:px-8 pt-8 pb-6">
        <div className="max-w-2xl">
          <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950 tracking-tight">
            常见问题
          </h1>
          <p className="mt-4 text-base leading-8 text-foreground-600">
            这里整理了读者最常问到的几个问题。如果下面没有你想知道的答案，
            欢迎直接到<Link to="/contact" className="text-primary-600 hover:underline">联系页面</Link>给我们留言。
          </p>
        </div>
      </section>

      <section className="max-w-container mx-auto px-4 md:px-8 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-9 space-y-12">
            {faqGroups.map((group) => (
              <section key={group.title}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className={`${group.icon} text-xl`} />
                  </span>
                  <h2 className="font-heading text-xl font-bold text-foreground-950">
                    {group.title}
                  </h2>
                </div>
                <div className="space-y-3">
                  {group.items.map((item) => (
                    <div
                      key={item.q}
                      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
                    >
                      <h3 className="font-heading text-base font-semibold text-foreground-950 flex gap-3">
                        <span className="shrink-0 text-primary-500 font-bold">Q</span>
                        {item.q}
                      </h3>
                      <p className="mt-3 pl-6 text-sm md:text-[15px] leading-8 text-foreground-600">
                        {item.a}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950 mb-3">
                  还有疑问？
                </h2>
                <p className="text-sm leading-7 text-foreground-600">
                  问题没解决的话，可以直接给我们留言，编辑部会尽快回复。
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary-600 font-medium hover:underline"
                >
                  前往联系页面
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </span>
                </Link>
              </div>
              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h2 className="font-heading text-sm font-semibold text-accent-950 mb-3">
                  想先读点内容
                </h2>
                <ul className="space-y-2.5 text-sm">
                  <li>
                    <Link to="/category/guquan-guancha" className="text-accent-900 hover:text-primary-600 transition-colors">
                      瓜圈观察 · 围观方法论
                    </Link>
                  </li>
                  <li>
                    <Link to="/category/wangluo-regeng" className="text-accent-900 hover:text-primary-600 transition-colors">
                      网络热梗 · 传播路径
                    </Link>
                  </li>
                  <li>
                    <Link to="/category/reju-zong" className="text-accent-900 hover:text-primary-600 transition-colors">
                      热剧综 · 类型趋势
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}